import {Injectable} from "@angular/core";
import {Node} from "../../../../models/node.model";
import {NodeService} from "../../../data/node.service";
import {DataService} from "../../../data/data.service";
import {TrainrunService} from "../../../data/trainrun.service";
import {
  buildEdges,
  computeNeighbors,
  computeShortestPaths,
  topoSort,
} from "src/app/view/util/origin-destination-graph";
import {MathUtils} from "../../../../utils/math";

// Computed values for an origin/destination pair.
export type OriginDestination = {
  // Names.
  origin: string;
  originId: number;
  destination: string;
  destinationId: number;
  // Travel time in minutes.
  travelTime: number | undefined;
  // Number of transfers.
  transfers: number | undefined;
  // Total cost, including connection penalties (optimized value).
  totalCost: number | undefined;
  // If false, no path was found (other fields should be ignored).
  found: boolean;
};

@Injectable({
  providedIn: "root",
})
export class OriginDestinationService {
  constructor(
    private nodeService: NodeService,
    private dataService: DataService,
    private trainrunService: TrainrunService,
  ) {}

  /**
   * Return the origin/destination nodes used for output.
   *
   * If nodes are selected, we output those, otherwise,
   * we output all visible nodes.
   * Note that other nodes may be used for the calculation.
   */
  getODOutputNodes(): Node[] {
    const selectedNodes = this.nodeService.getSelectedNodes();
    return selectedNodes.length > 0 ? selectedNodes : this.nodeService.getVisibleNodes();
  }

  /**
   * Compute travelTime, transfers, and totalCost for all origin/destination pairs.
   *
   * Note that this is expensive.
   */
  originDestinationData(): OriginDestination[] {
    // Duration of the schedule to consider (in minutes).
    // TODO: ideally this would be 24 hours, but performance is a concern.
    // One idea to optimize would be to consider the minimum time window before the schedule repeats (LCM).
    // Draft here: https://colab.research.google.com/drive/1Z1r2uU2pgffWxCbG_wt2zoLStZKzWleE#scrollTo=F6vOevK6znee
    const timeLimit = 24 * 60;

    // The cost to add for each connection.
    const connectionPenalty =
      this.dataService.getAnalyticsSettings().originDestinationSettings.connectionPenalty;
    const nodes = this.nodeService.getNodes();
    const odNodes = this.getODOutputNodes();
    const trainruns = this.trainrunService.getVisibleTrainruns();

    const [edges, tsSuccessor] = buildEdges(
      nodes,
      odNodes,
      trainruns,
      connectionPenalty,
      this.trainrunService,
      timeLimit,
    );

    const neighbors = computeNeighbors(edges);
    const vertices = topoSort(neighbors);
    // In theory we could parallelize the pathfindings, but the overhead might be too big.
    const res = new Map<string, [number, number]>();
    odNodes.forEach((origin) => {
      computeShortestPaths(origin.getId(), neighbors, vertices, tsSuccessor).forEach(
        ([cost, connections, _trainrunSectionIds], key) => {
          res.set([origin.getId(), key].join(","), [cost, connections]);
        },
      );
    });

    const rows: OriginDestination[] = [];
    odNodes.sort((a, b) => a.getBetriebspunktName().localeCompare(b.getBetriebspunktName()));
    odNodes.forEach((origin) => {
      odNodes.forEach((destination) => {
        if (origin.getId() === destination.getId()) {
          return;
        }
        const costs = res.get([origin.getId(), destination.getId()].join(","));
        if (costs === undefined) {
          // Keep empty if no path is found.
          rows.push({
            origin: origin.getBetriebspunktName(),
            destination: destination.getBetriebspunktName(),
            originId: origin.getId(),
            destinationId: destination.getId(),
            found: false,
            travelTime: undefined,
            transfers: undefined,
            totalCost: undefined,
          });
          return;
        }
        const [totalCost, connections] = costs;
        const row = {
          origin: origin.getBetriebspunktName(),
          destination: destination.getBetriebspunktName(),
          originId: origin.getId(),
          destinationId: destination.getId(),
          travelTime: MathUtils.round(totalCost - connections * connectionPenalty, 10),
          transfers: MathUtils.round(connections, 10),
          totalCost: MathUtils.round(totalCost, 10),
          found: true,
        };
        rows.push(row);
      });
    });

    return rows;
  }
}
